import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  getUnseenNotifications,
  markNotificationSeen,
} from "../../services/notifications";
import { formatISTWallClock } from "../../utils/formatDate";
import { Button } from "./Button";

function BellEmptyIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export default function NotificationsDrawer({ tenantId, onClose, onSeen }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getUnseenNotifications(tenantId)
      .then((res) => {
        if (cancelled) return;
        setNotifications(
          res.data?.items || res.data?.notifications || res.data || [],
        );
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load notifications.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleNotificationClick = async (notification) => {
    const id = notification.id || notification.notification_id;
    setNotifications((prev) => {
      const next = prev.filter((n) => (n.id || n.notification_id) !== id);
      onSeen?.(next.length);
      return next;
    });
    try {
      await markNotificationSeen(id);
    } catch {
      // best-effort — notification stays cleared from the unseen list locally
    }
  };

  const handleMarkAllSeen = async () => {
    setNotifications([]);
    onSeen?.(0);
    try {
      await markNotificationSeen();
    } catch {
      // best-effort
    }
  };

  return (
    <>
      {/* Click-outside catcher */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      <div className="absolute right-0 top-full mt-2 w-80 h-[28rem] bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-gray-700"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <h2 className="text-sm font-semibold text-gray-800">
              Notifications
            </h2>
          </div>
          {notifications.length > 0 && (
            <Button
              variant="link"
              onClick={handleMarkAllSeen}
              className="!text-xs !text-teal-700"
            >
              Mark all as read
            </Button>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading && (
            <div className="p-6 text-sm text-gray-500 text-center">
              Loading…
            </div>
          )}

          {!loading && error && (
            <div className="p-6 text-sm text-[#C73838] text-center">
              {error}
            </div>
          )}

          {!loading && !error && notifications.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                <BellEmptyIcon />
              </div>
              <div className="text-sm font-medium text-gray-700">
                All caught up!
              </div>
              <div className="text-xs text-gray-400">No notifications yet</div>
            </div>
          )}

          {!loading && !error && notifications.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {notifications.map((n) => {
                const id = n.id || n.notification_id;
                return (
                  <li key={id}>
                    <button
                      onClick={() => handleNotificationClick(n)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-800">
                            {n.breach_title ||
                              n.title ||
                              n.message ||
                              n.description}
                          </div>
                          {(n.notified_at || n.created_at) && (
                            <div className="text-xs text-gray-400 mt-1">
                              {formatISTWallClock(
                                n.notified_at || n.created_at,
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

NotificationsDrawer.propTypes = {
  tenantId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSeen: PropTypes.func,
};
